import React, { useState, useEffect, useRef, useCallback } from 'react';
import { XMarkIcon, PaperAirplaneIcon, ExclamationTriangleIcon, ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline';
import { Link, useLocation } from 'react-router-dom';
import { useLanguage } from '../i18n';
import ReactMarkdown from 'react-markdown';
import { legalPaths } from '../legal/routes';
import {
  getContentTestingFromPath,
  isContentTestingPath,
  withFromPrice,
  withFromPriceList,
} from '../content-testing/routes';
import { getProduct, productPilotUrl } from '../products';

export type ChatAssistant = 'website' | 'content-testing';

const STORAGE_KEYS: Record<ChatAssistant, { messages: string; session: string }> = {
  website: {
    messages: 'tamerintech-chat-messages',
    session: 'tamerintech-chat-session-id',
  },
  'content-testing': {
    messages: 'tamerintech-scan-chat-messages',
    session: 'tamerintech-scan-chat-session-id',
  },
};

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface TurnstileRenderOptions {
  sitekey: string;
  callback: (token: string) => void;
  'error-callback'?: () => void;
  'expired-callback'?: () => void;
  execution?: 'render' | 'execute';
  appearance?: 'always' | 'execute' | 'interaction-only';
  size?: 'normal' | 'flexible' | 'compact';
  language?: string;
}

interface TurnstileApi {
  render: (container: HTMLElement, options: TurnstileRenderOptions) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

export const CHAT_API_URL = 'https://simplechat-kids-backend.azurewebsites.net/api/tamerinSendMessage';
export const CHAT_BOOKING_URL = 'https://calendly.com/tamerintech/30min';
export const CHAT_CONTACT_EMAIL = 'info@tamerin.tech';
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '0x4AAAAAAEtO_Gi-BZ0Z3mQB';
const TURNSTILE_SCRIPT = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const TOKEN_TIMEOUT_MS = 30000;
const MAX_INPUT_CHARS = 2000;

/** Tells the backend which site version and product focus this widget belongs to. */
const WIDGET_CONTEXT = { site: 'tamerin.tech', siteVersion: 'landing-2026', focus: 'contextfriend' } as const;

const newSessionId = () => `tamerin-web-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const greetingMessage = (text: string): Message => ({
  role: 'assistant',
  content: text,
  timestamp: new Date()
});

let turnstileScriptPromise: Promise<void> | null = null;
const loadTurnstileScript = (): Promise<void> => {
  if (window.turnstile) return Promise.resolve();
  if (!turnstileScriptPromise) {
    turnstileScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => {
        turnstileScriptPromise = null;
        reject(new Error('Turnstile script failed to load'));
      };
      document.head.appendChild(script);
    });
  }
  return turnstileScriptPromise;
};

/**
 * Website chat bubble
 *
 * Lead-oriented assistant focused on ContextFriend: it answers questions,
 * recommends the fitting next step and can hand the visitor's details to Ron.
 * Fixed next-step links (book a call, apply for a pilot, email) are always one
 * tap away, so a lead can convert even if the model does not get there.
 *
 * Sends the full history, the page language, a Cloudflare Turnstile token and
 * the widget context with every message; persists the conversation in
 * sessionStorage. The content-testing pages get their own assistant.
 */
interface ChatbotProps {
  navigationOpen?: boolean;
  onOpen?: () => void;
}

const Chatbot: React.FC<ChatbotProps> = ({ navigationOpen = false, onOpen }) => {
  const { language, t } = useLanguage();
  const location = useLocation();
  const scanPage = getContentTestingFromPath(location.pathname);
  const assistant: ChatAssistant = isContentTestingPath(location.pathname) ? 'content-testing' : 'website';
  const scanCurrency = scanPage?.currency ?? 'USD';
  const copy = t.chat;
  const greeting =
    assistant === 'content-testing'
      ? withFromPrice(t.contentTesting.chat.greeting, scanCurrency)
      : copy.greeting;
  const quickReplies =
    assistant === 'content-testing'
      ? withFromPriceList(t.contentTesting.chat.quickReplies, scanCurrency)
      : copy.quickReplies;
  const storage =
    assistant === 'content-testing'
      ? {
          messages: `tamerintech-scan-chat-messages-${scanCurrency}`,
          session: `tamerintech-scan-chat-session-id-${scanCurrency}`,
        }
      : STORAGE_KEYS.website;
  const pilotUrl = productPilotUrl(getProduct('contextfriend'), language, 'chat_cta');

  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [sessionId, setSessionId] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const launcherRef = useRef<HTMLButtonElement>(null);
  const turnstileContainerRef = useRef<HTMLDivElement>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const tokenResolver = useRef<{ resolve: (token: string) => void; reject: (error: Error) => void } | null>(null);

  // Session, greeting and history are per assistant so the testing page
  // never reuses the main-site conversation (or the other way around).
  useEffect(() => {
    const savedSessionId = sessionStorage.getItem(storage.session);
    if (savedSessionId) {
      setSessionId(savedSessionId);
    } else {
      const id = newSessionId();
      setSessionId(id);
      sessionStorage.setItem(storage.session, id);
    }
  }, [storage.session]);

  useEffect(() => {
    const savedMessages = sessionStorage.getItem(storage.messages);
    if (savedMessages) {
      try {
        const parsed = JSON.parse(savedMessages) as Array<{ role: 'user' | 'assistant'; content: string; timestamp: string }>;
        const loadedMessages = parsed.map((m) => ({ ...m, timestamp: new Date(m.timestamp) }));
        if (loadedMessages.length === 1 && loadedMessages[0].role === 'assistant') {
          setMessages([greetingMessage(greeting)]);
        } else {
          setMessages(loadedMessages);
        }
        return;
      } catch (error) {
        console.error('Failed to parse saved messages:', error);
      }
    }
    setMessages([greetingMessage(greeting)]);
  }, [greeting, storage.messages]);

  useEffect(() => {
    if (messages.length > 0) {
      sessionStorage.setItem(storage.messages, JSON.stringify(messages));
    }
  }, [messages, storage.messages]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isTyping]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    if (navigationOpen) setIsOpen(false);
  }, [navigationOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        launcherRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Render the Turnstile widget while the chat is open (invisible unless a challenge is needed)
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    loadTurnstileScript()
      .then(() => {
        if (cancelled || !window.turnstile || !turnstileContainerRef.current || turnstileWidgetId.current) return;
        turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          execution: 'execute',
          appearance: 'interaction-only',
          size: 'flexible',
          language,
          callback: (token) => {
            tokenResolver.current?.resolve(token);
            tokenResolver.current = null;
          },
          'error-callback': () => {
            tokenResolver.current?.reject(new Error('Turnstile error'));
            tokenResolver.current = null;
          },
          'expired-callback': () => {
            tokenResolver.current?.reject(new Error('Turnstile token expired'));
            tokenResolver.current = null;
          }
        });
      })
      .catch((error) => console.warn('Turnstile unavailable:', error));

    return () => {
      cancelled = true;
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.remove(turnstileWidgetId.current);
      }
      turnstileWidgetId.current = null;
      tokenResolver.current = null;
    };
  }, [isOpen, language]);

  /** Fresh single-use token for the next request; resolves to null if Turnstile is unavailable. */
  const getTurnstileToken = useCallback((): Promise<string | null> => {
    const widgetId = turnstileWidgetId.current;
    if (!widgetId || !window.turnstile) return Promise.resolve(null);
    return new Promise((resolve) => {
      const timeout = setTimeout(() => {
        tokenResolver.current = null;
        resolve(null);
      }, TOKEN_TIMEOUT_MS);
      tokenResolver.current = {
        resolve: (token) => {
          clearTimeout(timeout);
          resolve(token);
        },
        reject: () => {
          clearTimeout(timeout);
          resolve(null);
        }
      };
      window.turnstile?.reset(widgetId);
      window.turnstile?.execute(widgetId);
    });
  }, []);

  const sendText = useCallback(
    async (rawText: string) => {
      const text = rawText.trim().slice(0, MAX_INPUT_CHARS);
      if (!text || isTyping) return;

      const userMessage: Message = { role: 'user', content: text, timestamp: new Date() };
      const history = [...messages, userMessage];
      setMessages(history);
      setInput('');
      setIsTyping(true);

      try {
        const turnstileToken = await getTurnstileToken();
        const response = await fetch(CHAT_API_URL, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            messages: history.map((m) => ({ role: m.role, content: m.content })),
            sessionId,
            language,
            pageUrl: window.location.href,
            assistant,
            currency: scanCurrency,
            turnstileToken,
            ...WIDGET_CONTEXT
          })
        });

        const data = await response.json().catch(() => null);
        const serverText: string | undefined = data?.serverResponse?.content;

        if (!response.ok && !serverText) {
          throw new Error(`Backend error ${response.status}`);
        }

        if (data?.sessionId && data.sessionId !== sessionId) {
          setSessionId(data.sessionId);
          sessionStorage.setItem(storage.session, data.sessionId);
        }

        setMessages((prev) => [...prev, { role: 'assistant', content: serverText || copy.empty, timestamp: new Date() }]);
      } catch (error) {
        console.error('Chat error:', error);
        setMessages((prev) => [...prev, { role: 'assistant', content: copy.error, timestamp: new Date() }]);
      } finally {
        setIsTyping(false);
      }
    },
    [assistant, copy.empty, copy.error, getTurnstileToken, isTyping, language, messages, scanCurrency, sessionId, storage.session]
  );

  const handleSend = () => sendText(input);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = () => {
    setMessages([greetingMessage(greeting)]);
    sessionStorage.removeItem(storage.messages);
    const id = newSessionId();
    setSessionId(id);
    sessionStorage.setItem(storage.session, id);
  };

  const showQuickReplies = messages.length === 1 && messages[0].role === 'assistant' && !isTyping;

  return (
    <div data-testid="site-chatbot" data-assistant={assistant}>
      {/* Chat Window */}
      {isOpen && (
        <div
          className="lp-chat-window bg-white rounded-md shadow-xl border border-[#dae2eb] flex flex-col z-50"
          role="dialog"
          aria-label={copy.title}
        >
          {/* Header */}
          <div className="bg-[#036fa7] text-white p-4 rounded-t-md flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center p-1">
                <img src="/chatbot-icon.jpg" alt="" className="w-full h-full object-contain rounded-full" />
              </div>
              <div>
                <h3 className="font-semibold text-base">{copy.title}</h3>
                <p className="text-xs text-blue-100">{copy.online}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleClearChat}
                className="text-white hover:bg-white/30 transition-colors text-xs px-2 py-1 bg-white/20 rounded"
                title={copy.clear}
              >
                {copy.clear}
              </button>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="text-white hover:text-blue-100 transition-colors"
                aria-label={copy.close}
              >
                <XMarkIcon className="w-6 h-6" aria-hidden="true" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-[#f7faff]">
            {/* AI Disclaimer */}
            <div className="flex items-start gap-2 p-3 bg-white border border-[#dae2eb] rounded">
              <ExclamationTriangleIcon className="w-5 h-5 text-[#036fa7] flex-shrink-0 mt-0.5" aria-hidden="true" />
              <p className="text-xs text-[#58677a]">
                {copy.notice}{' '}
                <Link to={legalPaths[language].privacy} className="underline font-medium hover:text-[#036fa7]">
                  {copy.privacyLink}
                </Link>
                {copy.noticeEnd}
              </p>
            </div>

            {messages.map((message, index) => (
              <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-xl px-4 py-3 ${
                    message.role === 'user'
                      ? 'bg-[#036fa7] text-white rounded-br-none'
                      : 'bg-white border border-[#dae2eb] text-[#18283b] rounded-bl-none'
                  }`}
                >
                  {message.role === 'user' ? (
                    <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                  ) : (
                    <div className="text-sm prose prose-sm max-w-none prose-p:my-2 prose-headings:my-2 prose-ul:my-2 prose-ol:my-2 prose-li:my-0 prose-a:text-[#036fa7]">
                      <ReactMarkdown>{message.content}</ReactMarkdown>
                    </div>
                  )}
                  <p className={`text-xs mt-1 ${message.role === 'user' ? 'text-blue-100' : 'text-gray-400'}`}>
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}

            {/* Quick replies for the first turn */}
            {showQuickReplies && (
              <div className="flex flex-wrap gap-2" data-testid="chat-quick-replies">
                {quickReplies.map((reply) => (
                  <button
                    key={reply}
                    type="button"
                    onClick={() => sendText(reply)}
                    className="text-sm px-3 py-2 rounded-full border border-[#95b9d5] bg-white text-[#036fa7] hover:bg-[#edf5ff] transition-colors text-left"
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}

            {/* Typing Indicator */}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-white border border-[#dae2eb] rounded-xl rounded-bl-none px-4 py-3">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                </div>
              </div>
            )}

            {/* Cloudflare Turnstile (invisible unless a challenge is required) */}
            <div ref={turnstileContainerRef} className="empty:hidden" />

            <div ref={messagesEndRef} />
          </div>

          {/* Fixed next steps: the lead can always convert without the model */}
          {assistant === 'website' && (
            <div className="px-4 py-2 border-t border-[#dae2eb] bg-white flex flex-wrap items-center gap-x-3 gap-y-1" data-testid="chat-cta">
              <span className="text-[10px] uppercase tracking-wider font-semibold text-[#526a82]">{copy.ctaLabel}</span>
              <a
                href={CHAT_BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs font-semibold text-[#036fa7] hover:underline underline-offset-2"
              >
                {copy.ctaBook} ↗
              </a>
              {pilotUrl && (
                <a
                  href={pilotUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs font-semibold text-[#036fa7] hover:underline underline-offset-2"
                >
                  {copy.ctaPilot} ↗
                </a>
              )}
              <a
                href={`mailto:${CHAT_CONTACT_EMAIL}`}
                className="text-xs font-semibold text-[#036fa7] hover:underline underline-offset-2"
              >
                {copy.ctaEmail}
              </a>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-[#dae2eb] bg-white rounded-b-md">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value.slice(0, MAX_INPUT_CHARS))}
                onKeyDown={handleKeyPress}
                placeholder={copy.placeholder}
                aria-label={copy.placeholder}
                maxLength={MAX_INPUT_CHARS}
                className="min-w-0 flex-1 px-4 py-3 border border-[#bfccda] rounded focus:outline-none focus:ring-2 focus:ring-[#036fa7] focus:border-transparent text-base"
                disabled={isTyping}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || isTyping}
                className="bg-[#036fa7] text-white p-3 rounded hover:bg-[#075985] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                aria-label="Send message"
              >
                <PaperAirplaneIcon className="w-5 h-5" aria-hidden="true" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chat bubble */}
      <button
        ref={launcherRef}
        type="button"
        onClick={() => {
          if (!isOpen) onOpen?.();
          setIsOpen(!isOpen);
        }}
        className="lp-chat-launcher"
        title={copy.open}
        aria-label={isOpen ? copy.close : copy.open}
        aria-expanded={isOpen}
      >
        <ChatBubbleLeftRightIcon aria-hidden="true" />
      </button>
    </div>
  );
};

export default Chatbot;
